import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { ReviewCard } from '../../ReviewsPage/components';
import ProductCard from '../../ResalePage/ProductCard';

import css from './SearchResultsPanel.module.css';

const REVIEW_LISTING_TYPE = 'review';
const MOBILE_BREAKPOINT = 767;
const LARGE_DESKTOP_BREAKPOINT = 1281;
const DEFAULT_SORT = 'createdAt';

const getCardsPerRow = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 5;
  }

  if (window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches) {
    return 2;
  }

  if (window.matchMedia(`(min-width: ${LARGE_DESKTOP_BREAKPOINT}px)`).matches) {
    return 5;
  }

  return 4;
};

const chunkItems = (items, chunkSize) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const rows = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    rows.push(items.slice(index, index + chunkSize));
  }

  return rows;
};

const getCreatedAtTime = listing => {
  const createdAt = listing?.attributes?.createdAt;
  return createdAt ? new Date(createdAt).getTime() : 0;
};

const getPriceAmount = listing => Number(listing?.attributes?.price?.amount || 0);

const sortListings = (items, sortKey) => {
  if (sortKey === 'relevance') {
    return items;
  }

  const sortedItems = [...items];

  switch (sortKey) {
    case 'createdAt':
      return sortedItems.sort((left, right) => getCreatedAtTime(right) - getCreatedAtTime(left));
    case '-createdAt':
      return sortedItems.sort((left, right) => getCreatedAtTime(left) - getCreatedAtTime(right));
    case 'price':
      return sortedItems.sort((left, right) => getPriceAmount(right) - getPriceAmount(left));
    case '-price':
      return sortedItems.sort((left, right) => getPriceAmount(left) - getPriceAmount(right));
    default:
      return items;
  }
};

const getSortOptions = (config, intl) => {
  const options = config?.search?.sortConfig?.options || [];

  return options.map(option => ({
    key: option.key,
    label: option?.labelTranslationKey
      ? intl.formatMessage({ id: option.labelTranslationKey })
      : option.label,
    longLabel: option?.labelTranslationKeyLong
      ? intl.formatMessage({ id: option.labelTranslationKeyLong })
      : option.longLabel,
  }));
};

const SectionSort = props => {
  const { ariaLabel, options, value, onChange } = props;

  return (
    <label className={css.sortControl}>
      <select
        aria-label={ariaLabel}
        className={css.sortSelect}
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        {options.map(option => (
          <option key={option.key} value={option.key}>
            {option.longLabel || option.label}
          </option>
        ))}
      </select>
    </label>
  );
};

const SectionCount = ({ count }) => {
  const label = count === 1 ? '1 result shown' : `${count} results shown`;
  return <span className={css.sectionCount}>{label}</span>;
};

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listings = [],
    search,
    isMapVariant = true,
    noResultsInfo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const initialSort = search?.sort || DEFAULT_SORT;
  const [resaleSort, setResaleSort] = useState(initialSort);
  const [reviewSort, setReviewSort] = useState(initialSort);
  const [cardsPerRow, setCardsPerRow] = useState(getCardsPerRow);

  useEffect(() => {
    setResaleSort(initialSort);
    setReviewSort(initialSort);
  }, [initialSort, search?.keywords]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateCardsPerRow = () => {
      setCardsPerRow(getCardsPerRow());
    };

    updateCardsPerRow();
    window.addEventListener('resize', updateCardsPerRow);

    return () => window.removeEventListener('resize', updateCardsPerRow);
  }, []);

  const sortOptions = useMemo(() => getSortOptions(config, intl), [config, intl]);
  const reviewListings = useMemo(
    () => listings.filter(listing => listing?.attributes?.publicData?.listingType === REVIEW_LISTING_TYPE),
    [listings]
  );
  const resaleListings = useMemo(
    () => listings.filter(listing => listing?.attributes?.publicData?.listingType !== REVIEW_LISTING_TYPE),
    [listings]
  );
  const sortedReviewListings = useMemo(
    () => sortListings(reviewListings, reviewSort),
    [reviewListings, reviewSort]
  );
  const sortedResaleListings = useMemo(
    () => sortListings(resaleListings, resaleSort),
    [resaleListings, resaleSort]
  );
  const reviewRows = useMemo(
    () => chunkItems(sortedReviewListings, cardsPerRow),
    [cardsPerRow, sortedReviewListings]
  );
  const searchTerm = typeof search?.keywords === 'string' ? search.keywords.trim() : '';
  const searchTermDisplay = searchTerm ? `"${searchTerm}"` : 'this search.';
  const noVisibleResults = sortedResaleListings.length === 0 && sortedReviewListings.length === 0;

  if (isMapVariant) {
    return (
      <div className={classes}>
        <div className={css.listingCardsMapVariant}>
          {listings.map(listing =>
            listing.attributes.publicData.listingType === REVIEW_LISTING_TYPE ? (
              <ReviewCard key={listing.id.uuid} listing={listing} />
            ) : (
              <ProductCard key={listing.id.uuid} listing={listing} />
            )
          )}
          {props.children}
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <section className={css.heroSection}>
        <p className={css.heroEyebrow}>Search</p>
        <h1 className={css.heroTitle}>
          <span className={css.heroTitleText}>Search results for</span>{' '}
          <span className={css.heroTitleAccent}>{searchTermDisplay}</span>
        </h1>
      </section>

      <section className={css.sectionBlock}>
        <div
          className={`${css.sectionHead} ${
            sortedResaleListings.length === 0 ? css.sectionHeadEmpty : ''
          }`}
        >
          <div>
            <p className={`${css.sectionLabel} ${css.sectionLabelSecondary}`}>OWN IT NOW</p>
            <div className={css.sectionTitleRow}>
              <h2 className={css.sectionTitle}>Resale</h2>
              <SectionCount count={sortedResaleListings.length} />
            </div>
          </div>
          {sortedResaleListings.length > 0 ? (
            <SectionSort
              ariaLabel="Sort resale results"
              options={sortOptions}
              value={resaleSort}
              onChange={setResaleSort}
            />
          ) : null}
        </div>

        {sortedResaleListings.length > 0 ? (
          <div className={css.resaleGrid}>
            {sortedResaleListings.map(listing => (
              <div key={listing.id.uuid} className={css.resaleCard}>
                <ProductCard listing={listing} />
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className={`${css.sectionBlock} ${css.sectionBlockAlt}`}>
        <div
          className={`${css.sectionHead} ${
            sortedReviewListings.length === 0 ? css.sectionHeadEmpty : ''
          }`}
        >
          <div>
            <p className={`${css.sectionLabel} ${css.sectionLabelPrimary}`}>HONEST TAKES</p>
            <div className={css.sectionTitleRow}>
              <h2 className={css.sectionTitle}>Reviews</h2>
              <SectionCount count={sortedReviewListings.length} />
            </div>
          </div>
          {sortedReviewListings.length > 0 ? (
            <SectionSort
              ariaLabel="Sort review results"
              options={sortOptions}
              value={reviewSort}
              onChange={setReviewSort}
            />
          ) : null}
        </div>

        {sortedReviewListings.length > 0 ? (
          <div className={css.reviewGrid}>
            {reviewRows.map((row, rowIndex) => (
              <div key={`review-row-${rowIndex}`} className={`${css.reviewGridRow} ${css.reviewCardGridRow}`}>
                {row.map(listing => (
                  <div key={listing.id.uuid} className={css.reviewCard}>
                    <ReviewCard listing={listing} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {noVisibleResults && noResultsInfo ? <div className={css.noResultsInfo}>{noResultsInfo}</div> : null}
    </div>
  );
};

export default SearchResultsPanel;
